import React, { useState, useEffect } from 'react';
import { kontakService } from '../../../services/kontakService';
import {
    FaSearch, FaEye, FaTrash, FaTimes, FaCheckCircle,
    FaExclamationTriangle, FaEnvelope, FaClock, FaChevronLeft,
    FaChevronRight, FaReply, FaArchive, FaUser, FaFilter
} from 'react-icons/fa';

const statusConfig = {
    baru: { label: 'Baru', color: 'bg-blue-100 text-blue-700', icon: FaEnvelope },
    dibaca: { label: 'Dibaca', color: 'bg-yellow-100 text-yellow-700', icon: FaEye },
    dibalas: { label: 'Dibalas', color: 'bg-green-100 text-green-700', icon: FaReply },
    arsip: { label: 'Arsip', color: 'bg-gray-100 text-gray-500', icon: FaArchive },
};

const ManajemenKontak = () => {
    const [kontakList, setKontakList] = useState([]);
    const [statistics, setStatistics] = useState({});
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedKontak, setSelectedKontak] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [notification, setNotification] = useState(null);
    const [catatan, setCatatan] = useState('');

    const loadData = async (page = 1) => {
        setLoading(true);
        const params = { page };
        if (searchTerm) params.search = searchTerm;
        if (filterStatus) params.status = filterStatus;

        const result = await kontakService.getList(params);
        if (result.success) {
            setKontakList(result.data.data || []);
            setTotalPages(result.data.last_page || 1);
            setCurrentPage(result.data.current_page || 1);
        }
        setLoading(false);
    };

    const loadStatistics = async () => {
        const result = await kontakService.getStatistics();
        if (result.success) setStatistics(result.data);
    };

    useEffect(() => {
        loadData(1);
        loadStatistics();
    }, []);

    const showNotif = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    const handleOpenDetail = async (kontak) => {
        const result = await kontakService.getDetail(kontak.id);
        if (result.success) {
            setSelectedKontak(result.data);
            setCatatan(result.data.catatan_admin || '');
            loadStatistics();
            loadData(currentPage);
        }
    };

    const handleUpdateStatus = async (id, status) => {
        const result = await kontakService.updateStatus(id, status, catatan);
        if (result.success) {
            showNotif('Status berhasil diubah');
            setSelectedKontak(null);
            loadData(currentPage);
            loadStatistics();
        } else {
            showNotif(result.message, 'error');
        }
    };

    const handleDelete = async () => {
        if (!deleteConfirm) return;
        const result = await kontakService.delete(deleteConfirm.id);
        if (result.success) {
            showNotif('Kontak berhasil dihapus');
            loadData(currentPage);
            loadStatistics();
        } else {
            showNotif(result.message, 'error');
        }
        setDeleteConfirm(null);
    };

    // ✅ Escape output untuk mencegah XSS saat preview
    const escapeHtml = (text) => {
        if (!text) return '';
        return String(text)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const StatusBadge = ({ status }) => {
        const config = statusConfig[status] || statusConfig.baru;
        const Icon = config.icon;
        return (
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
                <Icon className="mr-1" /> {config.label}
            </span>
        );
    };

    return (
        <div>
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-[#0F4C3A]">Manajemen Kontak</h2>
                    <p className="text-gray-600">Kelola pesan dari pengunjung website</p>
                </div>
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                {[
                    { label: 'Total', value: statistics.total, color: 'text-[#0F4C3A]' },
                    { label: 'Baru', value: statistics.baru, color: 'text-blue-600' },
                    { label: 'Dibaca', value: statistics.dibaca, color: 'text-yellow-600' },
                    { label: 'Dibalas', value: statistics.dibalas, color: 'text-green-600' },
                    { label: 'Arsip', value: statistics.arsip, color: 'text-gray-500' },
                ].map((stat, i) => (
                    <div key={i} className="bg-white rounded-lg p-3 shadow-sm text-center">
                        <p className={`text-2xl font-bold ${stat.color}`}>{stat.value || 0}</p>
                        <p className="text-xs text-gray-500">{stat.label}</p>
                    </div>
                ))}
            </div>

            {notification && (
                <div className={`mb-4 p-4 rounded-lg flex items-center ${notification.type === 'success'
                        ? 'bg-green-50 border border-green-200 text-green-700'
                        : 'bg-red-50 border border-red-200 text-red-700'
                    }`}>
                    {notification.type === 'success'
                        ? <FaCheckCircle className="mr-2" />
                        : <FaExclamationTriangle className="mr-2" />}
                    {notification.message}
                </div>
            )}

            {/* Filter */}
            <div className="bg-white rounded-xl p-4 shadow-sm mb-6 flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Cari nama, email, subjek, atau pesan..."
                        className="w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                    />
                </div>
                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                >
                    <option value="">Semua Status</option>
                    {Object.keys(statusConfig).map(k => (
                        <option key={k} value={k}>{statusConfig[k].label}</option>
                    ))}
                </select>
                <button onClick={() => loadData(1)} className="btn-primary">
                    <FaFilter className="inline mr-1" /> Filter
                </button>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr className="text-left text-sm text-gray-500">
                                <th className="p-4">Pengirim</th>
                                <th className="p-4">Subjek / Pesan</th>
                                <th className="p-4">Tanggal</th>
                                <th className="p-4">Status</th>
                                <th className="p-4 text-center">Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="p-8 text-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2E7D32] mx-auto"></div>
                                    </td>
                                </tr>
                            ) : kontakList.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="p-8 text-center text-gray-500">
                                        Belum ada pesan kontak
                                    </td>
                                </tr>
                            ) : (
                                kontakList.map((item) => (
                                    <tr key={item.id} className="border-b hover:bg-gray-50">
                                        <td className="p-4">
                                            <div className="flex items-center">
                                                <div className="w-10 h-10 bg-gradient-to-br from-[#0F4C3A] to-[#2E7D32] rounded-full flex items-center justify-center text-white font-bold mr-3 flex-shrink-0">
                                                    {item.nama?.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-700">{escapeHtml(item.nama)}</p>
                                                    <p className="text-xs text-gray-500">{item.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-4 max-w-md">
                                            {item.subjek && (
                                                <p className="font-medium text-gray-700 text-sm mb-1">
                                                    {escapeHtml(item.subjek)}
                                                </p>
                                            )}
                                            <p className="text-sm text-gray-500 line-clamp-2">
                                                {escapeHtml(item.pesan)}
                                            </p>
                                        </td>
                                        <td className="p-4 text-sm text-gray-500">
                                            {formatDate(item.created_at)}
                                        </td>
                                        <td className="p-4">
                                            <StatusBadge status={item.status} />
                                        </td>
                                        <td className="p-4">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    onClick={() => handleOpenDetail(item)}
                                                    className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"
                                                    title="Lihat Detail"
                                                >
                                                    <FaEye />
                                                </button>
                                                <button
                                                    onClick={() => setDeleteConfirm(item)}
                                                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                                    title="Hapus"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t">
                        <p className="text-sm text-gray-500">Halaman {currentPage} dari {totalPages}</p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => loadData(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                            >
                                <FaChevronLeft />
                            </button>
                            <button
                                onClick={() => loadData(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                            >
                                <FaChevronRight />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            {selectedKontak && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl max-w-2xl w-full my-8 max-h-[calc(100vh-4rem)] flex flex-col">
                        {/* Header */}
                        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white rounded-t-2xl z-10">
                            <h3 className="text-xl font-bold text-[#0F4C3A]">Detail Pesan</h3>
                            <button
                                onClick={() => setSelectedKontak(null)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <FaTimes size={20} />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-6 overflow-y-auto flex-1 space-y-4">
                            {/* Sender */}
                            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                                <div className="w-12 h-12 bg-gradient-to-br from-[#0F4C3A] to-[#2E7D32] rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                                    {selectedKontak.nama?.charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-gray-800">{escapeHtml(selectedKontak.nama)}</p>
                                    <p className="text-sm text-gray-600 truncate">{selectedKontak.email}</p>
                                    <p className="text-xs text-gray-400 mt-1">
                                        <FaClock className="inline mr-1" />
                                        {formatDate(selectedKontak.created_at)}
                                    </p>
                                </div>
                                <StatusBadge status={selectedKontak.status} />
                            </div>

                            {/* Subjek */}
                            {selectedKontak.subjek && (
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Subjek</label>
                                    <p className="text-gray-800 bg-gray-50 p-3 rounded-lg">
                                        {escapeHtml(selectedKontak.subjek)}
                                    </p>
                                </div>
                            )}

                            {/* Pesan */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Pesan</label>
                                <div className="text-gray-700 bg-gray-50 p-4 rounded-lg whitespace-pre-wrap break-words">
                                    {escapeHtml(selectedKontak.pesan)}
                                </div>
                            </div>

                            {/* Info */}
                            <div className="grid grid-cols-2 gap-4 text-xs text-gray-500">
                                <div>
                                    <span className="font-medium">IP Address:</span>
                                    <p>{selectedKontak.ip_address || '-'}</p>
                                </div>
                                <div>
                                    <span className="font-medium">Dibaca:</span>
                                    <p>{selectedKontak.read_at ? formatDate(selectedKontak.read_at) : 'Belum'}</p>
                                </div>
                            </div>

                            {/* Catatan Admin */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Catatan Internal (Opsional)
                                </label>
                                <textarea
                                    value={catatan}
                                    onChange={(e) => setCatatan(e.target.value)}
                                    rows="3"
                                    maxLength="1000"
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                    placeholder="Catatan untuk internal (tidak dilihat pengirim)..."
                                />
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex flex-wrap justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-2xl">
                            <button
                                onClick={() => handleUpdateStatus(selectedKontak.id, 'dibaca')}
                                className="px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors text-sm"
                            >
                                <FaEye className="inline mr-1" /> Tandai Dibaca
                            </button>
                            <button
                                onClick={() => handleUpdateStatus(selectedKontak.id, 'dibalas')}
                                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
                            >
                                <FaReply className="inline mr-1" /> Tandai Dibalas
                            </button>
                            <button
                                onClick={() => handleUpdateStatus(selectedKontak.id, 'arsip')}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                            >
                                <FaArchive className="inline mr-1" /> Arsipkan
                            </button>
                            <a
                                href={`mailto:${selectedKontak.email}?subject=Re: ${encodeURIComponent(selectedKontak.subjek || 'Pesan Anda')}`}
                                className="px-4 py-2 bg-[#2E7D32] text-white rounded-lg hover:bg-[#4CAF50] transition-colors text-sm"
                            >
                                <FaEnvelope className="inline mr-1" /> Balas via Email
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirm */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FaTrash className="text-3xl text-red-500" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">Hapus Pesan?</h3>
                        <p className="text-gray-600 mb-6">
                            Pesan dari <strong>{escapeHtml(deleteConfirm.nama)}</strong> akan dihapus permanen.
                        </p>
                        <div className="flex justify-center gap-3">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                className="px-6 py-2.5 border rounded-lg text-gray-700 hover:bg-gray-50"
                            >
                                Batal
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-6 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                                Hapus
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManajemenKontak;